import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type UserlikeChatbotModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/UserlikeChatbotModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import UserlikeChatbotModuleActions from './UserlikeChatbotModuleActions';
import UserlikeChatbotSettingsEmptyList from './UserlikeChatbotSettingsEmptyList';
import UserlikeChatbotSettingsTable from './UserlikeChatbotSettingsTable';

export type UserlikeChatbotModulePageProps = { module: UserlikeChatbotModuleWithPermissionsSpecsFragment };

const UserlikeChatbotModulePage = ({ module }: UserlikeChatbotModulePageProps) => {
    const { t } = useTranslation(['userlikeChatbotModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('userlikeChatbotModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('userlikeChatbotModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<UserlikeChatbotModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('userlikeChatbotModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <Divider>{t('userlikeChatbotModuleDetails:dividers.mainDetails')}</Divider>
            {module.chatbotSettings.length === 0 ? (
                <UserlikeChatbotSettingsEmptyList module={module} />
            ) : (
                <UserlikeChatbotSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default UserlikeChatbotModulePage;
